const Expertise = () => {
  const solutions = [
    {
      id: 'knowledge-management',
      title: 'Knowledge Management',
      description: 'Organize and discover information across your organization through intelligent systems that connect data, documents, and institutional knowledge.',
      features: [
        'Intelligent document classification and tagging systems',
        'Cross-collection search and discovery platforms',
        'Automated metadata extraction and enhancement',
        'Knowledge graph construction for complex relationships',
        'Institutional memory preservation and access tools',
        'Collaborative annotation and curation workflows'
      ]
    },
    {
      id: 'information-extraction',
      title: 'Information Extraction',
      description: 'Extract key data from documents and unstructured sources using advanced AI techniques to unlock hidden value in your collections.',
      features: [
        'OCR and document digitization with high accuracy',
        'Named entity recognition for people, places, and organizations',
        'Relationship extraction between historical events and figures',
        'Automatic transcription of handwritten documents',
        'Multi-language text processing and translation',
        'Structured data creation from unstructured sources'
      ]
    },
    {
      id: 'ai-agents',
      title: 'AI Agents',
      description: 'Build intelligent assistants that interact with users naturally, providing personalized access to collections and expert knowledge.',
      features: [
        'Conversational interfaces for collection exploration',
        'Research assistant bots for scholars and visitors',
        'Multilingual support for diverse audiences',
        'Context-aware recommendations and suggestions',
        'Integration with existing collection management systems',
        'Continuous learning from user interactions'
      ]
    },
    {
      id: 'natural-language-processing',
      title: 'Natural Language Processing',
      description: 'Understand and analyze human language at scale to make textual collections more searchable, accessible, and meaningful.',
      features: [
        'Semantic search across documents and collections',
        'Automatic summarization of lengthy historical texts',
        'Sentiment analysis of historical correspondence',
        'Topic modeling for thematic collection organization',
        'Language detection and automatic translation',
        'Text similarity analysis for duplicate detection'
      ]
    },
    {
      id: 'machine-learning',
      title: 'Machine Learning',
      description: 'Learn from your data to uncover patterns, make predictions, and automate complex decision-making processes.',
      features: [
        'Predictive modeling for collection preservation needs',
        'Automated quality assessment of digitized materials',
        'User behavior analysis for improved experiences',
        'Recommendation systems for related materials',
        'Anomaly detection for security and preservation',
        'Custom model training on institutional data'
      ]
    },
    {
      id: 'business-automation',
      title: 'Business Automation',
      description: 'Streamline repetitive tasks and reduce manual errors through intelligent automation tailored to cultural institutions.',
      features: [
        'Automated cataloging and metadata workflows',
        'Digital asset management and processing pipelines',
        'Grant reporting and compliance automation',
        'Visitor registration and engagement tracking',
        'Collection condition monitoring and alerts',
        'Staff scheduling and resource allocation systems'
      ]
    },
    {
      id: 'cloud-technologies',
      title: 'Cloud Technologies',
      description: 'Scale and secure your systems with modern cloud infrastructure designed for cultural heritage preservation and access.',
      features: [
        'Scalable storage solutions for growing digital collections',
        'Content delivery networks for global access',
        'Disaster recovery and backup strategies',
        'Secure authentication and authorization systems',
        'Cost-effective infrastructure scaling',
        'Integration with existing on-premises systems'
      ]
    },
    {
      id: 'data-visualization',
      title: 'Data Visualization',
      description: 'Transform complex data into clear, interactive visual stories that make collections and research more engaging and accessible.',
      features: [
        'Interactive timelines and historical narratives',
        'Geographic mapping of collection items and events',
        'Network visualizations of relationships and connections',
        'Statistical dashboards for collection analytics',
        'Customizable public-facing data exploration tools',
        'Accessible design for diverse audiences'
      ]
    }
  ];

  return (
    <div>
      <div className="max-w-4xl mx-auto mb-12">
        <h1 className="text-4xl font-bold mb-6">Our Solutions</h1>
        <p className="text-lg text-glam-secondary dark:text-gray-300">
          We leverage cutting-edge technology to solve the unique challenges facing 
          cultural institutions. Our technical solutions are designed to preserve, 
          organize, and share your collections while enhancing visitor engagement 
          and scholarly research.
        </p>
      </div>

      <div className="space-y-16">
        {solutions.map((solution) => (
          <section key={solution.id} id={solution.id} className="scroll-mt-20">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-4">{solution.title}</h2>
              <p className="text-lg text-glam-secondary dark:text-gray-300 mb-8">
                {solution.description}
              </p>
              <div className="bg-glam-light dark:bg-gray-900 rounded-lg p-8">
                <h3 className="text-xl font-semibold mb-4">Key Capabilities</h3>
                <ul className="space-y-3">
                  {solution.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <svg 
                        className="w-6 h-6 text-glam-accent mr-3 mt-0.5 flex-shrink-0" 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M5 13l4 4L19 7" 
                        />
                      </svg>
                      <span className="text-glam-secondary dark:text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        ))}
      </div>

      <section className="mt-16 max-w-4xl mx-auto">
        <div className="bg-glam-accent text-white rounded-lg p-8 text-center">
          <h2 className="text-2xl font-bold mb-4">
            Technology Tailored to Your Mission
          </h2>
          <p className="mb-6">
            Every institution has unique technical needs. We combine proven technologies 
            with custom development to create solutions that fit your workflow, 
            budget, and long-term goals.
          </p>
          <a
            href="/contact"
            className="inline-block px-6 py-3 bg-white text-glam-accent rounded-lg hover:bg-gray-100 transition-colors font-semibold"
          >
            Explore Technical Solutions
          </a>
        </div>
      </section>
    </div>
  );
};

export default Expertise;